/**
 * Item 271: Accessible Modal Wrapper
 *
 * Drop-in replacement for the ad-hoc `fixed inset-0 ... bg-black/70` pattern
 * used across 30+ modal components. Provides:
 *  - role="dialog" aria-modal="true" aria-labelledby (WCAG 2.1 AA)
 *  - useFocusTrap: Tab/Shift+Tab cycles only through focusable children
 *  - Escape key dismissal
 *  - Backdrop click-to-close
 *  - AnimatePresence entry/exit animation
 *
 * Usage:
 * ```tsx
 * <Modal isOpen={isOpen} onClose={onClose} titleId="my-modal-title">
 *   <h2 id="my-modal-title">Title</h2>
 *   ...content...
 * </Modal>
 * ```
 */
import { AnimatePresence, motion } from 'motion/react';
import { createPortal } from 'react-dom';
import { isTopFocusTrap, useFocusTrap } from '@/hooks/useFocusTrap';
import { useGlobalShortcut } from '@/hooks/useGlobalShortcut';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Must match the `id` of the element providing the dialog title (aria-labelledby) */
    titleId: string;
    /** Optional id of the dialog's explanatory text. */
    descriptionId?: string;
    children: React.ReactNode;
    /** Max-width class for the inner panel. Defaults to 'max-w-2xl' */
    maxWidth?: string;
    /** Additional class names for the inner panel */
    className?: string;
    /** Stable callable dialogs must remain visible above full-screen workspaces. */
    portal?: boolean;
    overlayZIndex?: number;
}

export function Modal({ isOpen, onClose, titleId, descriptionId, children, maxWidth = 'max-w-2xl', className = '', portal = false, overlayZIndex }: ModalProps) {
    const trapRef = useFocusTrap(isOpen);

    // Escape key closes modal
    useGlobalShortcut({
        id: 'ui-modal-escape',
        key: 'Escape',
        priority: 'modal',
        ignoreInput: true,
        handler: (event) => {
            if (!isTopFocusTrap(trapRef.current)) return;
            event.preventDefault();
            event.stopPropagation();
            onClose();
        }
    }, [onClose], isOpen);

    const content = (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                    aria-describedby={descriptionId}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 bg-black/70 backdrop-blur-sm overscroll-contain sm:items-center"
                    style={overlayZIndex === undefined ? undefined : { zIndex: overlayZIndex }}
                    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
                >
                    <motion.div
                        ref={trapRef}
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className={`my-auto max-h-[calc(100dvh-2rem)] w-full ${maxWidth} overflow-x-hidden overflow-y-auto overscroll-contain rounded-2xl border border-white/10 bg-slate-900 shadow-2xl ${className}`}
                    >
                        {children}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
    return portal && typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}
